/* Flourishing Fragrances — Compare Scents with custom Scent picker.
   Uses existing Master Scent metaobjects. Independent of product variant-picker code.
   Native <dialog> supplies modal focus behavior and Escape-to-close.
*/
(function () {
  'use strict';

  function element(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = String(text);
    return el;
  }

  function readNotes(value) {
    try {
      const parsed = JSON.parse(value || 'null');
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String).join(' · ');
      if (parsed === null || parsed === undefined) return '';
      return String(parsed).trim();
    } catch (_) {
      return String(value || '').trim();
    }
  }

  function safeColor(color) {
    const raw = String(color || '').trim();
    return /^#[0-9a-f]{3,8}$/i.test(raw) ? raw : '#d8d8d8';
  }

  function getCatalog(root) {
    const unique = new Set();
    return Array.from(root.querySelectorAll('[data-ffc-catalog] [data-ffc-scent]'))
      .map(el => ({
        id: el.dataset.id || '',
        name: el.dataset.name || '',
        pickerName: el.dataset.pickerName || el.dataset.name || '',
        color: safeColor(el.dataset.color),
        type: el.dataset.type || '',
        family: el.dataset.family || '',
        profile: el.dataset.profile || '',
        vibe: el.dataset.vibe || '',
        inspiration: el.dataset.inspiration || '',
        single: el.dataset.single || '',
        top: readNotes(el.dataset.top),
        heart: readNotes(el.dataset.heart),
        base: readNotes(el.dataset.base),
        description: el.dataset.description || '',
        image: el.dataset.image || '',
        url: el.dataset.url || ''
      }))
      .filter(scent => {
        if (!scent.id || !scent.name || !scent.url || unique.has(scent.id)) return false;
        unique.add(scent.id);
        return true;
      })
      .sort((a, b) => a.pickerName.localeCompare(b.pickerName, undefined, { sensitivity: 'base' }));
  }

  function formatType(type) {
    const normalized = String(type || '').toLowerCase().replace(/[_-]/g, ' ').trim();
    if (normalized === 'single note') return 'Single Note';
    if (normalized === 'inspired' || normalized === 'inspired by') return 'Inspired By';
    if (normalized === 'fragrance free') return 'Fragrance Free';
    if (normalized === 'original') return 'Original';
    return String(type || '').replace(/_/g, ' ');
  }

  function setText(el, value) {
    if (!el) return;
    const content = String(value || '').trim();
    el.textContent = content || '—';
    el.classList.toggle('ffc__missing', !content);
  }

  // Large images are intentionally removed from the compare panel.
  function drawHeader(target, scent) {
    if (!target) return;
    target.replaceChildren(element('h2', 'ffc__scent-name', scent.name));
    if (scent.type) target.append(element('p', 'ffc__kind', formatType(scent.type)));
  }

  function init(root) {
    if (!root || root.dataset.ffcReady === 'true') return;
    const openButtons = {
      first: root.querySelector('[data-ffc-open="first"]'),
      second: root.querySelector('[data-ffc-open="second"]')
    };
    const dialog = root.querySelector('[data-ffc-dialog]');
    const closeButton = root.querySelector('[data-ffc-close]');
    const search = root.querySelector('[data-ffc-search]');
    const tabs = Array.from(root.querySelectorAll('[data-ffc-tab]'));
    const filters = root.querySelector('[data-ffc-filters]');
    const list = root.querySelector('[data-ffc-options]');
    const count = root.querySelector('[data-ffc-count]');
    const noResults = root.querySelector('[data-ffc-no-results]');
    const contextLabel = root.querySelector('[data-ffc-picker-context]');
    const swap = root.querySelector('[data-ffc-swap]');
    const comparison = root.querySelector('[data-ffc-comparison]');
    const empty = root.querySelector('[data-ffc-empty]');
    if (!openButtons.first || !openButtons.second || !dialog || !closeButton || !search ||
        !filters || !list || !count || !noResults || !contextLabel || !swap || !comparison || !empty) return;
    root.dataset.ffcReady = 'true';

    const catalog = getCatalog(root);
    const byId = new Map(catalog.map(scent => [scent.id, scent]));
    // Never choose fragrances on the customer's behalf. A new visit starts empty.
    // Preserve deliberate choices across reloads of this page in the same tab.
    const chosen = { first: '', second: '' };
    const storageKey = 'ff-compare-scents:v1:' + window.location.pathname + ':' + root.id;

    function restoreChoices() {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
        if (!saved || typeof saved !== 'object') return;
        if (typeof saved.first === 'string' && byId.has(saved.first)) chosen.first = saved.first;
        if (typeof saved.second === 'string' && byId.has(saved.second) && saved.second !== chosen.first) {
          chosen.second = saved.second;
        }
      } catch (_) {
        // Storage can be unavailable in private or restricted browsing modes.
      }
    }

    function saveChoices() {
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(chosen));
      } catch (_) {
        // Browsing and comparing still work without session storage.
      }
    }

    restoreChoices();
    let editing = null;
    let activeTab = 'all';
    let activeFilter = '';
    // Return visible keyboard focus after a keyboard choice, but don't leave
    // the picker looking selected/focused after a mouse or touch choice.
    let pointerDismissal = false;

    function renderTrigger(key) {
      const scent = byId.get(chosen[key]);
      const label = root.querySelector('[data-ffc-trigger-name="' + key + '"]');
      const meta = root.querySelector('[data-ffc-trigger-meta="' + key + '"]');
      const dot = root.querySelector('[data-ffc-trigger-dot="' + key + '"]');
      if (!label || !meta || !dot) return;
      label.textContent = scent ? scent.pickerName : 'Select a scent';
      meta.textContent = scent ? [scent.family, scent.profile].filter(Boolean).join(' · ') : '';
      meta.hidden = !meta.textContent;
      dot.style.backgroundColor = scent ? scent.color : '#d8d8d8';
      dot.hidden = !scent;
      openButtons[key].classList.toggle('has-selection', !!scent);
    }

    function renderComparison() {
      renderTrigger('first');
      renderTrigger('second');
      const a = byId.get(chosen.first);
      const b = byId.get(chosen.second);
      const ready = a && b && a.id !== b.id;
      swap.disabled = !ready;
      comparison.hidden = !ready;
      empty.hidden = !!ready;
      if (!ready) {
        empty.textContent = catalog.length < 2
          ? (catalog.length ? 'Add one more visible Master Scent entry to start comparing.'
                            : 'No scents are available to compare yet. Check your Master Scent entries.')
          : (!chosen.first && !chosen.second)
            ? 'Choose your first and second scents to see their comparison.'
            : (!chosen.first ? 'Choose your first scent to continue.'
                             : (!chosen.second ? 'Choose your second scent to see the comparison.'
                                               : 'Choose two different scents to compare.'));
        return;
      }
      drawHeader(root.querySelector('[data-ffc-header="first"]'), a);
      drawHeader(root.querySelector('[data-ffc-header="second"]'), b);
      setText(root.querySelector('[data-ffc-column="first"]'), a.name);
      setText(root.querySelector('[data-ffc-column="second"]'), b.name);
      ['family', 'profile', 'vibe', 'inspiration', 'single', 'top', 'heart', 'base', 'description'].forEach(field => {
        const row = root.querySelector('[data-ffc-row="' + field + '"]');
        if (!row) return;
        row.hidden = !String(a[field] || '').trim() && !String(b[field] || '').trim();
        if (!row.hidden) {
          setText(row.querySelector('[data-ffc-value="first"]'), a[field]);
          setText(row.querySelector('[data-ffc-value="second"]'), b[field]);
        }
      });
      const linkA = root.querySelector('[data-ffc-link="first"]');
      const linkB = root.querySelector('[data-ffc-link="second"]');
      linkA.href = a.url;
      linkB.href = b.url;
      linkA.textContent = 'View ' + a.name;
      linkB.textContent = 'View ' + b.name;
    }

    function filterValue(scent) {
      if (activeTab === 'family') return scent.family.trim();
      if (activeTab === 'profile') return scent.profile.trim();
      if (activeTab === 'type') return formatType(scent.type).trim();
      return '';
    }

    function renderFilters() {
      filters.replaceChildren();
      if (activeTab === 'all') {
        filters.hidden = true;
        return;
      }
      const options = Array.from(new Set(catalog.map(filterValue).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      filters.hidden = !options.length;
      if (!options.length) return;
      if (activeFilter && !options.includes(activeFilter)) activeFilter = '';
      ['All', ...options].forEach((value, index) => {
        const option = index === 0 ? '' : value;
        const btn = element('button', 'ffc__picker-filter' + (activeFilter === option ? ' is-active' : ''), value);
        btn.type = 'button';
        btn.setAttribute('aria-pressed', String(activeFilter === option));
        btn.addEventListener('click', () => {
          activeFilter = option;
          renderFilters();
          renderOptions();
        });
        filters.append(btn);
      });
    }

    function renderOptions() {
      list.replaceChildren();
      const query = search.value.trim().toLowerCase();
      const shown = catalog.filter(scent => {
        if (activeFilter && filterValue(scent) !== activeFilter) return false;
        if (!query) return true;
        return [scent.pickerName, scent.name, scent.family, scent.profile, scent.type,
                scent.vibe, scent.inspiration, scent.description].some(value => String(value || '').toLowerCase().includes(query));
      });
      count.textContent = shown.length + (shown.length === 1 ? ' scent' : ' scents');
      noResults.hidden = shown.length > 0;
      shown.forEach(scent => {
        const selected = scent.id === chosen[editing];
        const item = element('button', 'ffc__picker-item' + (selected ? ' is-selected' : ''));
        item.type = 'button';
        item.setAttribute('aria-pressed', String(selected));
        item.dataset.ffcOption = scent.id;

        const visual = element('span', 'ffc__picker-item-visual');
        // Use only the scent's color dot, matching the product-page selector.
        // Do not render the illustrative single-note icon in this picker.
        const dot = element('span', 'ffc__picker-item-dot');
        dot.style.backgroundColor = scent.color;
        dot.setAttribute('aria-hidden', 'true');
        visual.append(dot);

        const copy = element('span', 'ffc__picker-item-copy');
        copy.append(element('span', 'ffc__picker-item-name', scent.pickerName));
        const metaText = [scent.family, scent.profile].filter(Boolean).join(' · ');
        if (metaText) copy.append(element('span', 'ffc__picker-item-meta', metaText));
        if (scent.inspiration) copy.append(element('span', 'ffc__picker-item-inspiration', 'Inspired by: ' + scent.inspiration));
        const marker = element('span', 'ffc__picker-item-marker', selected ? 'Selected' : '');
        marker.setAttribute('aria-hidden', 'true');
        item.append(visual, copy, marker);
        item.addEventListener('click', (event) => {
          pointerDismissal = event.detail > 0;
          const other = editing === 'first' ? 'second' : 'first';
          if (chosen[other] === scent.id) chosen[other] = chosen[editing];
          chosen[editing] = scent.id;
          saveChoices();
          renderComparison();
          dialog.close();
        });
        list.append(item);
      });
    }

    function openPicker(key) {
      if (!dialog.showModal) return;
      editing = key;
      pointerDismissal = false;
      contextLabel.textContent = key === 'first' ? 'FIRST SCENT' : 'SECOND SCENT';
      activeTab = 'all';
      activeFilter = '';
      search.value = '';
      tabs.forEach(tab => {
        const isActive = tab.dataset.ffcTab === 'all';
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', String(isActive));
      });
      renderFilters();
      renderOptions();
      dialog.showModal();
      openButtons[key].setAttribute('aria-expanded', 'true');
      // Do not autofocus search: mobile customers should not get an unexpected keyboard.
      closeButton.focus({ preventScroll: true });
    }

    Object.entries(openButtons).forEach(([key, button]) => {
      button.addEventListener('click', () => openPicker(key));
    });
    closeButton.addEventListener('click', event => {
      pointerDismissal = event.detail > 0;
      dialog.close();
    });
    dialog.addEventListener('click', event => {
      // Clicks on the dialog's actual backdrop hit the dialog itself.
      if (event.target === dialog) {
        pointerDismissal = event.detail > 0;
        dialog.close();
      }
    });
    dialog.addEventListener('close', () => {
      Object.values(openButtons).forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (editing && openButtons[editing]) {
        const trigger = openButtons[editing];
        trigger.focus({ preventScroll: true });
        // The native dialog restores focus to the trigger on close. For mouse
        // and touch selections, blur it so Safari doesn't keep the heavy
        // :focus-visible ring / grey fill shown until the next click.
        // Keep focus for Enter, Space, or Escape keyboard interactions.
        if (pointerDismissal) trigger.blur();
      }
      pointerDismissal = false;
      editing = null;
    });
    search.addEventListener('input', renderOptions);
    tabs.forEach(tab => tab.addEventListener('click', () => {
      activeTab = tab.dataset.ffcTab;
      activeFilter = '';
      tabs.forEach(item => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', String(isActive));
      });
      renderFilters();
      renderOptions();
    }));
    swap.addEventListener('click', () => {
      const temp = chosen.first;
      chosen.first = chosen.second;
      chosen.second = temp;
      saveChoices();
      renderComparison();
    });

    renderComparison();
    if (catalog.length < 2) {
      Object.values(openButtons).forEach(button => { button.disabled = true; });
      swap.disabled = true;
    }
  }

  function initializeAll(context) {
    if (context.matches && context.matches('[data-ffc-root]')) init(context);
    context.querySelectorAll('[data-ffc-root]').forEach(init);
  }
  if (!window.__ffcCompareListeners) {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
    document.addEventListener('shopify:section:load', event => initializeAll(event.target));
    window.__ffcCompareListeners = true;
  }
  initializeAll(document);
})();
